import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext, RewardMethod } from "../../../context/AppContext";
import { useNespresso } from "../context/NespressoContext";

// Nespresso brand colors
const NESPRESSO = {
  brown: "#986f38",
  brownHover: "#815e2f",
  gold: "#c69f36",
  black: "#000000",
  darkGray: "#292929",
  lightGray: "#f9f9f9",
  border: "#e6e5e4",
};

const RewardSelectionView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { state: nespressoState, currentTier, nextTier, progressToNext, podsToNext } = useNespresso();

  // Calculate total credit and points from empties
  const totalCredit = state.empties.reduce((sum, item) => {
    const dollarValue = item.product.rewards?.dollarValue;
    if (dollarValue) {
      return sum + parseFloat(dollarValue.replace("$", ""));
    }
    return sum;
  }, 0);

  const totalPoints = state.empties.reduce((sum, item) => {
    return sum + (item.product.rewards?.loyaltyPoints || 0) * item.quantity;
  }, 0);

  // Get capsule count from empties
  const capsuleCount = state.empties.length;

  const handleSelectLoyaltyPoints = () => {
    dispatch({ type: "SET_REWARD_METHOD", payload: RewardMethod.LOYALTY_POINTS });
    navigate("/nespresso/summary");
  };

  const handleBack = () => {
    navigate("/nespresso/scan");
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: NESPRESSO.black }}
    >
      {/* Header */}
      <header className="py-4 px-8 flex items-center justify-between border-b border-white/10">
        <button
          onClick={handleBack}
          className="text-white/60 hover:text-white transition-colors flex items-center gap-2"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
        <div className="flex items-center gap-3">
          <span
            className="font-medium uppercase tracking-widest text-sm"
            style={{ color: NESPRESSO.gold }}
          >
            Nespresso Recycling Program
          </span>
        </div>
        <div className="text-white font-medium">
          {capsuleCount} capsules scanned
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <h1 className="text-4xl font-light text-white mb-3">
            Choose Your Reward
          </h1>
          <p className="text-lg text-white/60 font-light">
            How would you like to receive your recycling credit?
          </p>
        </motion.div>

        {/* Reward Option - Loyalty Points Only */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="max-w-md w-full"
        >
          <button
            onClick={handleSelectLoyaltyPoints}
            className="w-full bg-white/5 hover:bg-white/10 border-2 border-white/20 hover:border-white/40 rounded-lg p-8 text-left transition-all duration-300 group"
          >
            <div className="flex items-start gap-6">
              {/* Icon */}
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${NESPRESSO.gold}20` }}
              >
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ color: NESPRESSO.gold }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                  />
                </svg>
              </div>

              {/* Content */}
              <div className="flex-1">
                <h3 className="text-white font-medium text-xl mb-2">
                  Nespresso Club Points
                </h3>
                <p className="text-white/60 font-light mb-4">
                  Add loyalty points to your Nespresso Club account.
                  Redeem for free coffee, accessories, and exclusive member benefits.
                </p>

                {/* Points Display */}
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-white/40 text-sm">Points Earned</p>
                    <p
                      className="text-3xl font-medium"
                      style={{ color: NESPRESSO.gold }}
                    >
                      {totalPoints.toLocaleString()}
                    </p>
                  </div>
                  <div className="h-12 w-px bg-white/20" />
                  <div>
                    <p className="text-white/40 text-sm">Credit Value</p>
                    <p className="text-xl font-medium text-white">
                      ${totalCredit.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <svg
                className="w-6 h-6 text-white/40 group-hover:text-white transition-colors flex-shrink-0 mt-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </button>
        </motion.div>

        {/* Tier Progress Preview */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-8 text-center max-w-md w-full"
        >
          <div className="bg-white/5 rounded-lg p-6 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-white/40 text-sm">Current Tier</p>
                <p className="text-white font-medium">
                  {currentTier.name}
                </p>
              </div>
              {nextTier && (
                <div className="text-right">
                  <p className="text-white/40 text-sm">Next Tier</p>
                  <p className="text-white font-medium">
                    {nextTier.name}
                  </p>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            {nextTier && (
              <>
                <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-2">
                  <div
                    className="h-full transition-all duration-500"
                    style={{
                      width: `${progressToNext}%`,
                      backgroundColor: NESPRESSO.gold,
                    }}
                  />
                </div>
                <p className="text-white/40 text-xs">
                  {podsToNext} pods to{" "}
                  {nextTier.name}
                </p>
              </>
            )}

            {/* After This Session */}
            {capsuleCount > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-sm" style={{ color: NESPRESSO.gold }}>
                  After this session: {nespressoState.gamification.lifetimePods + capsuleCount} lifetime pods
                </p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Info Text */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-white/40 text-sm mt-6 font-light"
        >
          Points will be credited to your Nespresso Club account within 24 hours
        </motion.p>
      </main>

      {/* Footer */}
      <footer className="py-4 px-8 text-center text-white/40 text-sm border-t border-white/10 font-light">
        Powered by Pentatonic AI
      </footer>
    </div>
  );
};

export default RewardSelectionView;
